import { action } from '@ember/object';
import Service, { inject as service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency';
import moment from 'moment';
import { TrackedMap } from 'tracked-built-ins';

import { BTTP_TRIAL_CACHE_KEY, BTTP_TRIAL_DURATION_HOURS, bttpReportNameMap } from 'calendar/types/bttp';
import BttpResource from 'later/resources/bttp';
import { SegmentEventTypes } from 'later/utils/constants/segment-events';
import { fetch } from 'later/utils/fetch';
import { roundedHalfHourMow } from 'later/utils/time-slot-utils';

import type CacheService from '../cache';
import type { BttpReport } from 'calendar/types/bttp';
import type SocialProfileModel from 'later/models/social-profile';
import type TimeSlotModel from 'later/models/time-slot';
import type AppcuesService from 'later/services/appcues';
import type AuthService from 'later/services/auth';
import type ConfigService from 'later/services/calendar/config';
import type SelectedSocialProfilesService from 'later/services/selected-social-profiles';
import type SubscriptionsService from 'later/services/subscriptions';
import type { UntypedService, Maybe } from 'shared/types';

const AUTO_ENABLE_CACHE_KEY = 'autoEnable';
const CACHE_KEY = 'showBttp';
const MINIMUM_FOLLOWERS = 100;
const TOGGLED_BTTP_SEGMENT_EVENT = 'toggled-best-time-to-post';
const TOGGLE_STATES = Object.freeze({ ON: 'on', OFF: 'off' });

export default class BttpService extends Service {
  @service declare appcues: AppcuesService;
  @service declare auth: AuthService;
  @service declare cache: CacheService;
  @service('calendar/config') declare calendarConfig: ConfigService;
  @service declare segment: UntypedService;
  @service declare selectedSocialProfiles: SelectedSocialProfilesService;
  @service declare subscriptions: SubscriptionsService;
  @service declare userConfig: UntypedService;

  @tracked multipleBttpTrialExpired: boolean = false;
  @tracked hasAcceptedMultiBttpTrial: boolean = false;
  @tracked userClosedMultiBttpModal: boolean = false;

  // Note: Tracked mirror of the per-profile BTTP toggle state persisted in the cache.
  // The cache itself does not invalidate consumers when individual keys mutate, so we
  // mirror the toggle state here to drive reactivity (e.g. calendar re-rendering).
  private bttpToggleStates = new TrackedMap<string, boolean>();

  bttpResource = BttpResource.from(this, () => []);

  constructor(...args: Record<string, unknown>[]) {
    super(...args);
    this.autoEnableBttp.perform();
    this.multipleBttpTrialExpired = this.checkIfTrialHasExpired();
    this.hasAcceptedMultiBttpTrial = Boolean(this.cache.retrieve(BTTP_TRIAL_CACHE_KEY));
  }

  get allBttpReports(): Record<SocialProfileModel['id'], BttpReport> {
    return (this.bttpResource.fetchTask?.value as Record<SocialProfileModel['id'], BttpReport>) ?? {};
  }

  get requiredFollowerCount(): number {
    return MINIMUM_FOLLOWERS;
  }

  report(socialProfile: SocialProfileModel): Maybe<BttpReport> {
    return socialProfile.bttpReport || this.allBttpReports[socialProfile.id];
  }

  roundedTimeSlots(socialProfile: SocialProfileModel): number[] {
    return this.report(socialProfile)?.timeSlots?.map((timeSlot) => roundedHalfHourMow(timeSlot)) || [];
  }

  autoEnableCacheKey(profileId: string): string {
    return `${AUTO_ENABLE_CACHE_KEY}-${profileId}`;
  }

  toggleCacheKey(profileId: string): string {
    return `${CACHE_KEY}-${profileId}`;
  }

  receivedIgBttpDiscoveryCacheKey(profileId: string): string {
    return `receivedIgBttpDiscovery_${profileId}`;
  }

  shouldShowBttpDiscovery(socialProfile: SocialProfileModel): boolean {
    if (socialProfile?.isInstagram) {
      const hasSeenIgBttpDiscovery = this.cache.retrieve(this.receivedIgBttpDiscoveryCacheKey(socialProfile.id));
      return !hasSeenIgBttpDiscovery;
    }
    return !socialProfile?.receivedBttpDiscovery;
  }

  toggledOn(socialProfile: SocialProfileModel): boolean {
    // Note: Reading from the TrackedMap registers a per-key tracker so that
    // subsequent setBttpToggle calls invalidate consumers (e.g. cachedData on
    // the full-calendar component).
    const trackedValue = this.bttpToggleStates.get(socialProfile.id);
    if (trackedValue !== undefined) return trackedValue;
    return !!this.cache.retrieve<boolean>(this.toggleCacheKey(socialProfile.id));
  }

  shouldShow(socialProfile: SocialProfileModel): boolean {
    return this.toggledOn(socialProfile) && this.couldShow(socialProfile.profileType);
  }

  couldShow(profileType: string | undefined): boolean {
    return Boolean(this.canBttp(profileType) && this.isValidSocialProfile(profileType));
  }

  canBttp(profileType: string | undefined): boolean {
    switch (profileType) {
      case 'tiktok':
        return Boolean(this.auth.currentAccount.canTiktokBttp);
      case 'instagram':
        return Boolean(this.auth.currentAccount.canBttp);
      case 'facebook':
        return Boolean(this.auth.currentAccount.canAccessFacebookBttp);
      default:
        return false;
    }
  }

  isValidSocialProfile(profileType: string | undefined): boolean {
    return Boolean(!!profileType && bttpReportNameMap[profileType] !== undefined);
  }

  learnMoreLink(profileType: string | undefined): string {
    switch (profileType) {
      case 'tiktok':
        return 'https://help.later.com/hc/articles/7948787987863';
      case 'facebook':
      case 'instagram':
      default:
        return 'https://help.later.com/hc/articles/360042771694-Find-Your-Best-Times-to-Post-on-Instagram';
    }
  }

  findNextSlotFromUnix(socialProfile: SocialProfileModel, unixTime: number): Maybe<moment.Moment> {
    if (!socialProfile.timeSlots?.length) return;

    const timeZoneIdentifier = this.calendarConfig.timeZoneIdentifier || this.userConfig.currentTimeZone.identifier;

    const slotsAsMomentObjects = this.#getUpcomingSlotsAsMoments(
      socialProfile.timeSlots.filter((slot) => slot.isBttp),
      unixTime,
      timeZoneIdentifier
      // Note: Sort dates using moment's 'diff' function
    ).sort((a, b) => a.diff(b));

    const nextSlot = slotsAsMomentObjects.find(
      (bttpSlot: moment.Moment) => !bttpSlot.isBefore(moment().tz(timeZoneIdentifier))
    );

    return nextSlot;
  }

  seenBttpDiscovery(socialProfile: SocialProfileModel): void {
    if (socialProfile.isInstagram) {
      this.cache.add(this.receivedIgBttpDiscoveryCacheKey(socialProfile.id), true, {
        expiry: this.cache.expiry(30, 'days'),
        persist: true
      });
    } else {
      if (socialProfile) {
        socialProfile.receivedBttpDiscovery = true;
        socialProfile.save();
      }
    }
  }

  /**
   * Checks whether the 24-hour trial window stored in cache has elapsed.
   * The cache stores an ISO timestamp representing the trial expiry time.
   */
  checkIfTrialHasExpired(): boolean {
    const cachedTimestamp = this.cache.retrieve(BTTP_TRIAL_CACHE_KEY);
    if (!cachedTimestamp || typeof cachedTimestamp !== 'string') return false;

    return (this.multipleBttpTrialExpired = moment().isAfter(cachedTimestamp));
  }

  startTrial(): void {
    this.segment.track('started-multi-bttp-trial');
    this.autoEnableBttp.perform();

    const trialExpiryTime = moment().add(BTTP_TRIAL_DURATION_HOURS, 'hours').toISOString();

    this.cache.add(BTTP_TRIAL_CACHE_KEY, trialExpiryTime, {
      expiry: this.cache.maxExpiryDate(),
      persist: true
    });
    this.hasAcceptedMultiBttpTrial = true;
  }

  @action
  setBttpToggle(value: boolean, socialProfileId: string): void {
    this.cache.add(this.toggleCacheKey(socialProfileId), value, {
      expiry: this.cache.maxExpiryDate(),
      persist: true
    });
    this.bttpToggleStates.set(socialProfileId, value);
  }

  @action
  toggleBttp(): void {
    const socialProfile = this.selectedSocialProfiles.firstProfile;

    // Disable toggle if there are multiple profiles selected
    if (!socialProfile || this.selectedSocialProfiles.profiles.length > 1) return;

    const { ON, OFF } = TOGGLE_STATES;
    this.setBttpToggle(!this.toggledOn(socialProfile), socialProfile.id);
    this.segment.track(TOGGLED_BTTP_SEGMENT_EVENT, {
      new_state: this.toggledOn(socialProfile) ? ON : OFF,
      social_profile_handle: socialProfile?.nickname
    });
  }

  autoEnableBttp = task(async () => {
    const socialProfiles = this.selectedSocialProfiles.profiles.filter((profile) =>
      this.couldShow(profile.profileType)
    );
    if (!socialProfiles.length) return;

    await Promise.all(
      socialProfiles.map(async (socialProfile) => {
        const hasNotToggledBttp =
          isEmpty(this.cache.retrieve(this.toggleCacheKey(socialProfile.id))) && !this.toggledOn(socialProfile);
        const hasAttemptedAutoEnable = this.#hasAttemptedAutoEnable(socialProfile.id);
        if (
          hasNotToggledBttp &&
          this.couldShow(socialProfile.profileType) &&
          socialProfile?.autoEnableBttp &&
          this.auth.currentAccount.rolloutAutoEnableBttp &&
          !hasAttemptedAutoEnable
        ) {
          this.#cacheAutoEnableAttempt(socialProfile.id);
          await this.generateBttpReport.perform(socialProfile);
          this.setBttpToggle(true, socialProfile.id);
          this.appcues.track(SegmentEventTypes.BttpEnabled);
          this.segment.track(SegmentEventTypes.BttpEnabled, {
            social_profile_handle: socialProfile?.nickname,
            active_trial: !!this.subscriptions.hasActiveTrial
          });
        }
      })
    );
  });

  generateBttpReport = task(async (socialProfile: SocialProfileModel) => {
    const report = await fetch(`/api/v2/analytics_reports`, {
      method: 'POST',
      body: {
        analytics_report: {
          name: socialProfile.bttpReportName,
          social_profile_id: socialProfile.id
        }
      }
    });
    return report;
  });

  #hasAttemptedAutoEnable(socialProfileId: string): boolean {
    return !isEmpty(this.cache.retrieve(this.autoEnableCacheKey(socialProfileId)));
  }

  #cacheAutoEnableAttempt(socialProfileId: string): void {
    this.cache.add(this.autoEnableCacheKey(socialProfileId), true, {
      expiry: this.cache.expiry(1, 'day'),
      persist: true
    });
  }

  #getUpcomingSlotsAsMoments(
    eligibleBttpSlotList: TimeSlotModel[],
    unixTime: number,
    timeZoneIdentifier: string
  ): moment.Moment[] {
    return eligibleBttpSlotList.map((bttpSlot: TimeSlotModel) => {
      const scheduledTime = moment.unix(unixTime);
      const { wday, hour, minute } = bttpSlot;
      const nextBttpSlot = scheduledTime.clone().day(wday).hour(hour).minute(minute);

      // Note: If the slot is in the past, find the same slot in the upcoming week
      if (nextBttpSlot.isBefore(scheduledTime)) {
        nextBttpSlot.add(7, 'days');
      }

      return nextBttpSlot.utc(true).tz(timeZoneIdentifier);
    });
  }
}

declare module '@ember/service' {
  interface Registry {
    'calendar/bttp': BttpService;
  }
}
